import { useEffect, useState, useCallback } from "react";
import PortalLayout from "@/layouts/PortalLayout";
import { api } from "@/lib/api";
import { toast } from "sonner";
import ImageUpload from "@/components/ImageUpload";
import MarkdownEditor from "@/components/MarkdownEditor";
import { Plus, X, Edit3, Trash2, Megaphone, Users, Download, ExternalLink, BarChart3 } from "lucide-react";

function emptyForm() { return { title: "", description: "", cover_image: "", theme_color: "#9C2C44", status: "published", goal: 1000, share_slogan: "" }; }

function TimelineChart({ data }) {
  const max = Math.max(1, ...data.map((d) => d.count));
  return (
    <div className="mt-3">
      <div className="flex h-32 items-end gap-1">
        {data.map((d, i) => (
          <div key={i} className="group relative flex-1" title={`${d.date}: ${d.count} signature${d.count > 1 ? "s" : ""}`}>
            <div className="w-full rounded-t bg-wine-500 transition-all hover:bg-wine-700" style={{ height: `${(d.count / max) * 100}%`, minHeight: d.count > 0 ? "3px" : "1px", opacity: d.count > 0 ? 1 : 0.2 }} />
            {d.count > 0 && <div className="absolute -top-5 left-1/2 -translate-x-1/2 rounded bg-navy px-1 py-0.5 text-[9px] font-bold text-white opacity-0 group-hover:opacity-100">{d.count}</div>}
          </div>
        ))}
      </div>
      <div className="mt-1 flex justify-between text-[10px] text-neutral-400">
        <span>{data[0]?.date.slice(5)}</span>
        <span>30 derniers jours</span>
        <span>{data[data.length - 1]?.date.slice(5)}</span>
      </div>
    </div>
  );
}

export default function AdminPetitions() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [detail, setDetail] = useState(null);
  const [signatures, setSignatures] = useState([]);
  const [timeline, setTimeline] = useState([]);

  const load = useCallback(() => api.get("/admin/petitions").then((r) => setItems(r.data || [])).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") { setOpen(false); setDetail(null); } };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const openNew = () => { setEdit(null); setForm(emptyForm()); setOpen(true); };
  const openEdit = (p) => { setEdit(p); setForm({ ...emptyForm(), ...p }); setOpen(true); };
  const submit = async (e) => {
    e.preventDefault();
    try {
      if (edit) await api.patch(`/admin/petitions/${edit.id}`, form);
      else await api.post("/admin/petitions", form);
      toast.success(edit ? "Pétition mise à jour" : "Pétition créée");
      setOpen(false); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };
  const remove = async (p) => {
    if (!window.confirm(`Supprimer "${p.title}" ?`)) return;
    await api.delete(`/admin/petitions/${p.id}`); toast.success("Supprimée"); load();
  };
  const openDetail = async (p) => {
    setDetail(p);
    const [sigs, tl] = await Promise.all([
      api.get(`/admin/petitions/${p.id}/signatures`),
      api.get(`/admin/petitions/${p.id}/timeline`),
    ]);
    setSignatures(sigs.data || []);
    setTimeline(tl.data || []);
  };
  const exportCSV = () => {
    const headers = ["email", "first_name", "last_name", "organization", "message", "allow_publish_name", "newsletter_optin", "created_at"];
    const rows = signatures.map((s) => headers.map((h) => `"${String(s[h] ?? "").replace(/"/g, '""')}"`).join(","));
    const csv = headers.join(",") + "\n" + rows.join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `signatures-${detail.slug}.csv`;
    a.click();
  };

  return (
    <PortalLayout sidebar="admin">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold text-navy md:text-4xl"><Megaphone className="mr-2 inline h-7 w-7 text-wine-500" />Pétitions</h1>
          <p className="mt-1 text-neutral-600">Gérez vos pétitions publiques et exportez les signataires.</p>
        </div>
        <button onClick={openNew} className="inline-flex items-center gap-2 rounded-full bg-wine-500 px-5 py-2.5 text-sm font-semibold text-white hover:bg-wine-600" data-testid="petition-new-btn">
          <Plus className="h-4 w-4" />Nouvelle pétition
        </button>
      </div>

      <div className="mt-6 overflow-x-auto rounded-2xl border border-neutral-100 bg-white">
        <table className="w-full min-w-[700px] text-sm">
          <thead className="bg-wine-50/40 text-xs uppercase tracking-wider text-neutral-500">
            <tr><th className="px-4 py-3 text-left">Titre</th><th className="px-4 py-3 text-left">Slug</th><th className="px-4 py-3 text-left">Statut</th><th className="px-4 py-3 text-left">Signatures</th><th className="px-4 py-3 text-right">Actions</th></tr>
          </thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={5} className="px-4 py-10 text-center text-neutral-500">Aucune pétition</td></tr>}
            {items.map((p) => (
              <tr key={p.id} className="border-t border-neutral-100" data-testid={`admin-petition-row-${p.id}`}>
                <td className="px-4 py-3 font-semibold text-navy">{p.title}</td>
                <td className="px-4 py-3 font-mono text-xs text-neutral-500">/{p.slug}</td>
                <td className="px-4 py-3"><span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${p.status === "published" ? "bg-emerald-50 text-emerald-700" : p.status === "closed" ? "bg-neutral-100 text-neutral-600" : "bg-amber-50 text-amber-700"}`}>{p.status}</span></td>
                <td className="px-4 py-3"><span className="inline-flex items-center gap-1 text-sm font-semibold text-wine-700"><Users className="h-3.5 w-3.5" />{p.signatures_count}</span></td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end gap-1">
                    <button onClick={() => openDetail(p)} className="grid h-7 w-7 place-items-center rounded-full text-blue-600 hover:bg-blue-50" title="Voir signataires" data-testid={`petition-detail-${p.id}`}><BarChart3 className="h-3.5 w-3.5" /></button>
                    <a href={`/soutenez-nous/${p.slug}`} target="_blank" rel="noreferrer" className="grid h-7 w-7 place-items-center rounded-full text-neutral-500 hover:bg-neutral-100" title="Voir public"><ExternalLink className="h-3.5 w-3.5" /></a>
                    <button onClick={() => openEdit(p)} className="grid h-7 w-7 place-items-center rounded-full text-blue-600 hover:bg-blue-50" title="Modifier" data-testid={`petition-edit-${p.id}`}><Edit3 className="h-3.5 w-3.5" /></button>
                    <button onClick={() => remove(p)} className="grid h-7 w-7 place-items-center rounded-full text-rose-600 hover:bg-rose-50" title="Supprimer" data-testid={`petition-del-${p.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4" onClick={() => setOpen(false)}>
          <form onSubmit={submit} className="w-full max-w-2xl rounded-2xl bg-white p-6 shadow-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="admin-petition-modal">
            <div className="flex items-center justify-between">
              <h3 className="text-xl font-bold text-navy">{edit ? <><Edit3 className="mr-1 inline h-5 w-5" /> Modifier</> : <><Plus className="mr-1 inline h-5 w-5" /> Nouvelle pétition</>}</h3>
              <button type="button" onClick={() => setOpen(false)} className="text-neutral-400 hover:text-neutral-700"><X className="h-5 w-5" /></button>
            </div>
            <div className="mt-4 space-y-3">
              <input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Titre" className="w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="admin-petition-title" />
              <ImageUpload value={form.cover_image} onChange={(url) => setForm({ ...form, cover_image: url })} label="Image de couverture" testId="admin-petition-cover" />
              <div>
                <label className="text-[10px] font-bold uppercase text-neutral-500">Description (markdown)</label>
                <MarkdownEditor value={form.description} onChange={(v) => setForm({ ...form, description: v })} rows={6} testId="admin-petition-desc" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] font-bold uppercase text-neutral-500">Objectif (nb signatures)</label>
                  <input type="number" min="1" value={form.goal} onChange={(e) => setForm({ ...form, goal: parseInt(e.target.value) || 1000 })} className="mt-1 w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="admin-petition-goal" />
                </div>
                <div>
                  <label className="text-[10px] font-bold uppercase text-neutral-500">Slogan pour partage (1-2 lignes)</label>
                  <input value={form.share_slogan} onChange={(e) => setForm({ ...form, share_slogan: e.target.value })} placeholder="Ex: Soutenons une fiscalité juste pour la WineTech française." className="mt-1 w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="admin-petition-slogan" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] font-bold uppercase text-neutral-500">Couleur thème</label>
                  <input type="color" value={form.theme_color} onChange={(e) => setForm({ ...form, theme_color: e.target.value })} className="mt-1 h-10 w-full rounded-xl border border-neutral-200" data-testid="admin-petition-color" />
                </div>
                <div>
                  <label className="text-[10px] font-bold uppercase text-neutral-500">Statut</label>
                  <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="mt-1 w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="admin-petition-status">
                    <option value="draft">Brouillon</option>
                    <option value="published">Publiée</option>
                    <option value="closed">Clôturée</option>
                  </select>
                </div>
              </div>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button type="button" onClick={() => setOpen(false)} className="rounded-full border border-neutral-300 px-4 py-2 text-sm">Annuler</button>
              <button type="submit" className="rounded-full bg-wine-500 px-5 py-2 text-sm font-bold text-white hover:bg-wine-600" data-testid="admin-petition-save">{edit ? "Mettre à jour" : "Publier"}</button>
            </div>
          </form>
        </div>
      )}

      {detail && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4" onClick={() => setDetail(null)}>
          <div className="w-full max-w-4xl rounded-2xl bg-white p-6 shadow-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="petition-signatures-modal">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-xl font-bold text-navy">{detail.title}</h3>
                <p className="text-xs text-neutral-500">{signatures.length} signature{signatures.length > 1 ? "s" : ""}</p>
              </div>
              <div className="flex gap-2">
                <button onClick={exportCSV} className="inline-flex items-center gap-1 rounded-full bg-wine-500 px-3 py-1.5 text-xs font-bold text-white hover:bg-wine-600" data-testid="petition-export-csv"><Download className="h-3 w-3" /> Export CSV</button>
                <button onClick={() => setDetail(null)} className="text-neutral-400 hover:text-neutral-700"><X className="h-5 w-5" /></button>
              </div>
            </div>

            <div className="mt-5 rounded-xl border border-neutral-100 bg-wine-50/30 p-4">
              <div className="text-[11px] font-bold uppercase tracking-wider text-wine-700"><BarChart3 className="mr-1 inline h-3 w-3" />Évolution des signatures (30 derniers jours)</div>
              <TimelineChart data={timeline} />
            </div>

            <div className="mt-5 overflow-x-auto rounded-xl border border-neutral-100">
              <table className="w-full min-w-[700px] text-xs">
                <thead className="bg-wine-50/40 text-[10px] uppercase text-neutral-500">
                  <tr><th className="px-3 py-2 text-left">Email</th><th className="px-3 py-2 text-left">Nom</th><th className="px-3 py-2 text-left">Organisation</th><th className="px-3 py-2 text-left">Date</th><th className="px-3 py-2 text-left">Public</th><th className="px-3 py-2 text-left">Newsletter</th><th className="px-3 py-2 text-left">Message</th></tr>
                </thead>
                <tbody>
                  {signatures.map((s) => (
                    <tr key={s.id} className="border-t border-neutral-100">
                      <td className="px-3 py-2 font-mono">{s.email}</td>
                      <td className="px-3 py-2">{s.first_name} {s.last_name}</td>
                      <td className="px-3 py-2 text-neutral-600">{s.organization || "—"}</td>
                      <td className="px-3 py-2 text-neutral-500">{(s.created_at || "").slice(0, 16).replace("T", " ")}</td>
                      <td className="px-3 py-2">{s.allow_publish_name ? "✅" : "—"}</td>
                      <td className="px-3 py-2">{s.newsletter_optin ? "✅" : "—"}</td>
                      <td className="px-3 py-2 max-w-xs truncate text-neutral-600">{s.message || "—"}</td>
                    </tr>
                  ))}
                  {signatures.length === 0 && <tr><td colSpan={7} className="px-3 py-6 text-center text-neutral-500">Aucune signature</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </PortalLayout>
  );
}
